import * as React from 'react';

import { REPORT_STEPS } from 'bundles/enterprise-admin-analytics/components/DrawerPollingSteps';
import type { ReportDownload } from 'bundles/enterprise-admin-analytics/components/ReportsStatusProvider';

const PROGRESS_CONFIG = {
  INITIAL: 5, // Start at 5%
  FAST_PHASE_TARGET: 40, // Target progress at 10 minutes
  MAX: 90, // Cap at 90% until completion
} as const;

const FAST_PHASE_DURATION = 10 * 60 * 1000;
const PROGRESS_UPDATE_INTERVAL = 2000;
const DEFAULT_TIMEOUT = 4 * 60 * 60 * 1000; // 4 hours
/**
 * Calculate time-based fake progress with two-phase acceleration
 *
 * Phase 1 (0-10 min): Faster progress using cubic root curve to reach 50%
 * Phase 2 (10+ min): Slower progress using square root curve from 50% to 90%
 *
 * This provides immediate visible feedback while still allowing long timeouts.
 *
 */
const calculateTimeBasedProgress = (startTime: number, timeoutDuration: number): number => {
  const elapsedTime = Date.now() - startTime;

  // Phase 1: First 10 minutes - fast progress to 50%
  if (elapsedTime < FAST_PHASE_DURATION) {
    const fastPhaseRatio = elapsedTime / FAST_PHASE_DURATION;
    // Use cubic root for even faster initial progress
    const progressRatio = fastPhaseRatio ** (1 / 3);
    const progress =
      PROGRESS_CONFIG.INITIAL + progressRatio * (PROGRESS_CONFIG.FAST_PHASE_TARGET - PROGRESS_CONFIG.INITIAL);
    return Math.round(progress);
  }

  // Phase 2: After 10 minutes - slower progress from 50% to 90%
  const remainingTime = elapsedTime - FAST_PHASE_DURATION;
  const remainingTimeout = timeoutDuration - FAST_PHASE_DURATION;
  const remainingRatio = Math.min(remainingTime / remainingTimeout, 1);

  // Use square root for gradual progress
  const progressRatio = Math.sqrt(remainingRatio);
  const progress =
    PROGRESS_CONFIG.FAST_PHASE_TARGET + progressRatio * (PROGRESS_CONFIG.MAX - PROGRESS_CONFIG.FAST_PHASE_TARGET);

  // Cap at MAX (90%)
  return Math.min(Math.round(progress), PROGRESS_CONFIG.MAX);
};

type UseReportProgressUpdaterParams = {
  downloads: ReportDownload[];
  updateDownload: (id: string, updates: Partial<ReportDownload>) => void;
};

/**
 * Custom hook to update report download progress on a consistent interval
 *
 * Uses a single interval for all downloads to minimize overhead and batch updates.
 * Progress is calculated using a two-phase curve based on elapsed time.
 */
export const useReportProgressUpdater = ({ downloads, updateDownload }: UseReportProgressUpdaterParams) => {
  const progressIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  const activeDownloadsRef = React.useRef<ReportDownload[]>([]);

  const hasActiveDownloads = React.useMemo(
    () => downloads.some((d) => d.status === REPORT_STEPS.LOADING && d.startTime),
    [downloads]
  );

  React.useEffect(() => {
    activeDownloadsRef.current = downloads.filter((d) => d.status === REPORT_STEPS.LOADING && d.startTime);
  }, [downloads]);

  // This effect now only runs when the state of "has active downloads" changes
  React.useEffect(() => {
    // If there are active downloads and NO timer is running, create one.
    if (hasActiveDownloads && !progressIntervalRef.current) {
      progressIntervalRef.current = setInterval(() => {
        activeDownloadsRef.current.forEach((download) => {
          if (!download.startTime) return;
          const currentProgress = calculateTimeBasedProgress(download.startTime, DEFAULT_TIMEOUT);

          if (currentProgress !== download.progress) {
            updateDownload(download.id, { progress: currentProgress });
          }
        });
      }, PROGRESS_UPDATE_INTERVAL);
    }
    // If there are no active downloads but a timer is running, clear it.
    else if (!hasActiveDownloads && progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, [hasActiveDownloads, updateDownload]);

  React.useEffect(() => {
    const intervalId = progressIntervalRef.current;
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);
};
